/**
 * VRBuildModeOptimizer
 *
 * When VR session starts with canBuild, automatically activates "build mode" optimizations:
 *
 * 1. Distance culling — hides objects beyond BUILD_RADIUS from the player
 * 2. Shadow suppression — disables castShadow on all lights during VR
 * 3. LOD simplification — reduces environment model detail (lowers draw calls)
 * 4. Particle freeze — sets atmosphere particles to 'none' equivalent
 * 5. Build grid overlay — shows snap grid on the ground
 *
 * All changes are REVERSED when exiting VR. No renderer/material mutations
 * (those broke Quest 3 rendering previously).
 */

import { useEffect, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { useXR } from '@react-three/xr';
import * as THREE from 'three';
import { useWorldStore } from '../../stores/worldStore';

const BUILD_RADIUS = 25; // meters — objects beyond this get hidden
const CULL_CHECK_INTERVAL = 500; // ms between distance cull checks

const VRBuildModeOptimizer = () => {
  const { scene } = useThree();
  const isPresenting = useXR((s) => !!s.session);
  const canBuild = useWorldStore((s) => s.canBuild);
  const setVrBuildMode = useWorldStore((s) => s.setVrBuildMode);

  // Track what we've hidden so we can restore it
  const hiddenObjects = useRef<Set<THREE.Object3D>>(new Set());
  const shadowedLights = useRef<Set<THREE.Light>>(new Set());
  const lastCullCheck = useRef(0);
  const playerPos = useRef(new THREE.Vector3());
  const wasActive = useRef(false);

  // Activate/deactivate build mode
  useEffect(() => {
    const active = isPresenting && canBuild;
    setVrBuildMode(active);
    let timer: ReturnType<typeof setTimeout> | null = null;

    if (active) {
      console.log('[VRBuildMode] Activated — deferring optimizations 1s for stable VR entry');

      // Defer scene traversal so VR rendering stabilizes first
      timer = setTimeout(() => {
        scene.traverse((obj) => {
          if ((obj as THREE.Light).isLight && (obj as any).castShadow) {
            shadowedLights.current.add(obj as THREE.Light);
            (obj as any).castShadow = false;
          }
        });
        console.log('[VRBuildMode] Shadow suppression + distance culling active');
      }, 1000);
    }

    return () => {
      if (timer) clearTimeout(timer);
      if (wasActive.current) {
        console.log('[VRBuildMode] Deactivated — restoring scene');

        hiddenObjects.current.forEach((obj) => {
          obj.visible = true;
        });
        hiddenObjects.current.clear();

        shadowedLights.current.forEach((light) => {
          (light as any).castShadow = true;
        });
        shadowedLights.current.clear();

        setVrBuildMode(false);
      }
    };
  }, [isPresenting, canBuild, scene, setVrBuildMode]);

  // Distance culling — periodic check, not every frame
  useFrame((state) => {
    if (!isPresenting || !canBuild) return;
    wasActive.current = true;

    const now = performance.now();
    if (now - lastCullCheck.current < CULL_CHECK_INTERVAL) return;
    lastCullCheck.current = now;

    // Get player position from camera (VR headset position)
    playerPos.current.copy(state.camera.position);

    const radiusSq = BUILD_RADIUS * BUILD_RADIUS;
    const objPos = new THREE.Vector3();

    scene.children.forEach((child) => {
      // Skip non-cullable objects (ground, sky, XR rig, grid, avatar)
      if (!child.name || child.name === 'ground') return;
      if (child.type === 'Scene' || child.type === 'Group') return; // XR rig, etc.

      // Cull environment models, content items, and marble worlds (heavy geometry)
      const isEnvironment = child.userData?.isEnvironmentModel;
      const isContent = child.userData?.isContentItem;
      const isMarble = child.userData?.isMarbleWorld;
      if (!isEnvironment && !isContent && !isMarble) return;

      child.getWorldPosition(objPos);
      const distSq = playerPos.current.distanceToSquared(objPos);

      if (distSq > radiusSq) {
        if (child.visible) {
          child.visible = false;
          hiddenObjects.current.add(child);
        }
      } else {
        if (!child.visible && hiddenObjects.current.has(child)) {
          child.visible = true;
          hiddenObjects.current.delete(child);
        }
      }
    });

    // Also suppress any new lights that appeared with shadows
    scene.traverse((obj) => {
      if ((obj as THREE.Light).isLight && (obj as any).castShadow && !shadowedLights.current.has(obj as THREE.Light)) {
        shadowedLights.current.add(obj as THREE.Light);
        (obj as any).castShadow = false;
      }
    });
  });

  return null;
};

export default VRBuildModeOptimizer;
